import { type FC, useMemo, useRef } from "react";

import { useQuery } from "@apollo/client/react";

import { ProjectDraftingTemplateEditDocument } from "@app_schema";

import { Button } from "@styled/button";
import { DocumentFrame, DocumentPane } from "@styled/document_frame";
import { LoadingSpinner } from "@styled/loading_spinner";
import { ModeBanner } from "@styled/mode_banner";
import { NoticeLine } from "@styled/notice_line";
import { SavedMark } from "@styled/saved_mark";

import { DraftEditLayer } from "./draft_edit_layer";
import {
  DRAFT_FRAME_DOCUMENT,
  DRAFT_FRAME_SANDBOX,
  useDraftFrame,
} from "./use_draft_frame";
import { useTemplateParagraphEdit } from "./use_template_paragraph_edit";

const NO_FILLS: [] = [];
const NO_LOCKS: [] = [];

// Edit your template: his template's file drawn as the Report draws his case's,
// any paragraph of his edited in place. Changes reach new cases only; a tinted
// bar says so, with the way back to the case.
export const DraftTemplateEditView: FC<{
  projectId: string;
  hidden: boolean;
  onBack(): void;
}> = ({ projectId, hidden, onBack }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const { data } = useQuery(ProjectDraftingTemplateEditDocument, {
    variables: { id: projectId },
    skip: hidden,
    fetchPolicy: "cache-and-network",
  });
  const template = data?.project.draftingTemplateEdit;
  const slots = useMemo(
    () =>
      new Map(
        (template?.sections ?? []).map(({ marker, name }) => [marker, name]),
      ),
    [template?.sections],
  );
  const { state, root } = useDraftFrame({
    frameRef,
    fileId: template?.displayDocx?.id,
    url: template?.displayDocx?.url,
    sections: NO_FILLS,
    slots,
    writing: null,
    filling: false,
  });
  const edit = useTemplateParagraphEdit(projectId, template?.fileChecksum);

  return (
    <DocumentPane hidden={hidden}>
      <ModeBanner
        action={
          <Button type="button" color="white" size="sm" onClick={onBack}>
            Back to this case
          </Button>
        }
      >
        Editing your template · changes apply to new cases.
      </ModeBanner>
      {edit.saved && (
        <NoticeLine>
          <SavedMark />
        </NoticeLine>
      )}
      {state === "failed" && (
        <NoticeLine>Couldn&apos;t show your template.</NoticeLine>
      )}
      {state === "loading" && <LoadingSpinner />}
      <DocumentFrame
        title="Your template"
        sandbox={DRAFT_FRAME_SANDBOX}
        srcDoc={DRAFT_FRAME_DOCUMENT}
        frameRef={frameRef}
      />
      <DraftEditLayer
        edit={edit}
        locks={template?.draftParagraphLocks ?? NO_LOCKS}
        frameRef={frameRef}
        root={root}
        drawn={template?.displayDocx?.id}
        enabled={state === "ready" && !hidden}
        writing={null}
      />
    </DocumentPane>
  );
};
